/**
 * Shared styled components and sub-components used across the
 * rule diff preview and apply modals.
 */
import React from 'react';
import ReactJson from 'react-json-view';
import styled from 'styled-components';

/* ── Environment helpers ── */

/**
 * Maps REACT_APP_ENV values to the backend NAMESPACE identifiers.
 * The backend expects one of: atd, uat1, uat2, prod.
 */
export const ENV_TO_NAMESPACE = {
  local: 'atd',
  atd: 'atd',
  'tp-uat1': 'uat1',
  'tp-uat2': 'uat2',
  'tp-prod': 'prod',
};

/**
 * All backend environment identifiers and their display labels.
 */
export const ALL_ENVIRONMENTS = [
  ['atd', 'ATD'],
  ['uat1', 'UAT1'],
  ['uat2', 'UAT2'],
  ['prod', 'Prod'],
];

/**
 * Returns the backend namespace string for the running frontend.
 */
export const getCurrentNamespace = () =>
  ENV_TO_NAMESPACE[process.env.REACT_APP_ENV] || 'atd';

/**
 * Returns the human-readable label for a backend namespace.
 */
export const getEnvLabel = ns =>
  (ALL_ENVIRONMENTS.find(([key]) => key === ns) || [])[1] ||
  ns.toUpperCase();

/* ── Styled components ── */

export const ModalContainer = styled.div`
  width: 100%;
  max-height: 80vh;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 0 10px;
  box-sizing: border-box;
`;

export const SummarySection = styled.div`
  margin-bottom: 24px;
`;

export const SummaryTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-top: 12px;

  th,
  td {
    text-align: left;
    padding: 8px 12px;
    border-bottom: 1px solid #e0e0e0;
  }

  th {
    font-weight: 600;
    color: #555;
    font-size: 13px;
    text-transform: uppercase;
  }
`;

export const StatusBadge = styled.span`
  display: inline-block;
  padding: 2px 10px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  color: white;
  background: ${props => {
    switch (props.status) {
      case 'added':
        return '#4caf50';
      case 'modified':
        return '#ff9800';
      case 'removed':
        return '#f44336';
      default:
        return '#9e9e9e';
    }
  }};
`;

export const ChangedFields = styled.span`
  font-size: 12px;
  color: #777;
`;

export const ErrorItem = styled.li`
  background: #fff5f5;
  border-left: 4px solid #f44336;
  padding: 12px 16px;
  margin-bottom: 8px;
  border-radius: 0 4px 4px 0;
  list-style: none;
`;

export const ErrorRuleName = styled.div`
  font-weight: 600;
  color: #d32f2f;
  margin-bottom: 4px;
`;

export const ErrorDetail = styled.div`
  font-size: 13px;
  color: #555;
  margin-left: 8px;
`;

export const ErrorList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

export const SectionTitle = styled.h3`
  margin: 20px 0 8px 0;
  font-size: 16px;
  color: #333;
`;

export const RawDiffContainer = styled.div`
  padding: 10px 20px;
  max-height: 400px;
  overflow: auto;
  background: #fafafa;
  border-radius: 4px;
  border: 1px solid #e0e0e0;
  word-break: break-all;
  box-sizing: border-box;
  width: 100%;
`;

export const CloseButtonRow = styled.div`
  display: flex;
  justify-content: flex-end;
  padding: 12px 0 0 0;
`;

export const DirectionLabel = styled.div`
  font-size: 14px;
  color: #555;
  margin-bottom: 16px;

  strong {
    text-transform: uppercase;
  }
`;

/* ── Shared sub-components ── */

/**
 * Renders a summary table of rule diffs: added / modified / removed.
 * Accepts the diff object containing updated_rules and removed_rules.
 */
export function DiffSummary({ diff, emptyMessage }) {
  const { updated_rules = {}, removed_rules = [] } = diff || {};
  const updatedEntries = Object.entries(updated_rules);
  const hasChanges = updatedEntries.length > 0 || removed_rules.length > 0;

  if (!hasChanges) {
    return (
      <SummarySection>
        <SectionTitle>Summary</SectionTitle>
        <p>{emptyMessage || 'No rule changes detected.'}</p>
      </SummarySection>
    );
  }

  return (
    <SummarySection>
      <SectionTitle>Summary</SectionTitle>
      <SummaryTable>
        <thead>
          <tr>
            <th>Rule Name</th>
            <th>Status</th>
            <th>Changed Fields</th>
          </tr>
        </thead>
        <tbody>
          {updatedEntries.map(([ruleName, detail]) => (
            <tr key={ruleName}>
              <td>{ruleName}</td>
              <td>
                <StatusBadge status={detail.status}>
                  {detail.status}
                </StatusBadge>
              </td>
              <td>
                <ChangedFields>
                  {detail.changed_fields
                    ? detail.changed_fields.join(', ')
                    : '—'}
                </ChangedFields>
              </td>
            </tr>
          ))}
          {removed_rules.map(ruleName => (
            <tr key={ruleName}>
              <td>{ruleName}</td>
              <td>
                <StatusBadge status='removed'>removed</StatusBadge>
              </td>
              <td>
                <ChangedFields>—</ChangedFields>
              </td>
            </tr>
          ))}
        </tbody>
      </SummaryTable>
    </SummarySection>
  );
}

/**
 * Renders validation errors returned by the backend.
 */
export function ValidationErrors({ errors }) {
  return (
    <SummarySection>
      <SectionTitle>Validation Errors</SectionTitle>
      <ErrorList>
        {errors.map((entry, idx) => (
          <ErrorItem key={idx}>
            <ErrorRuleName>
              Rule #{entry.rule_index != null ? entry.rule_index : idx}
              {entry.rule_name ? ` — ${entry.rule_name}` : ''}
            </ErrorRuleName>
            {entry.errors &&
              entry.errors.map((msg, i) => (
                <ErrorDetail key={i}>• {msg}</ErrorDetail>
              ))}
          </ErrorItem>
        ))}
      </ErrorList>
    </SummarySection>
  );
}

/**
 * Renders the raw JSON response inside a scrollable, bordered container.
 */
export function RawJsonView({ src }) {
  return (
    <>
      <SectionTitle>Raw Response</SectionTitle>
      <RawDiffContainer>
        <ReactJson
          src={src}
          collapsed={2}
          displayDataTypes={false}
          name={false}
          enableClipboard
        />
      </RawDiffContainer>
    </>
  );
}
